export type InputStepId = 'company' | 'organization' | 'location' | 'person';
export type StepId = 'account' | InputStepId | 'review';

export type SetupTask = {
  id: StepId;
  title: string;
  defaultDetail: string;
};

export type InitialAdminSetupSession = {
  email: string;
  displayName: string;
  csrfToken: string;
};

export type InitialAdminSetupInput = {
  companyName: string;
  rootOrganizationName: string;
  locationName: string;
  legalFamilyName: string;
  legalGivenName: string;
  hireDate: string;
};

export interface InitialAdminSetupPort<TSession> {
  complete(input: InitialAdminSetupInput, csrfToken: string): Promise<TSession>;
}

export type InitialAdminSetupFailureReason = 'already-completed' | 'expired';

export class InitialAdminSetupFailure extends Error {
  constructor(
    readonly reason: InitialAdminSetupFailureReason,
    message: string,
  ) {
    super(message);
    this.name = 'InitialAdminSetupFailure';
  }
}

export const inputSteps: readonly InputStepId[] = ['company', 'organization', 'location', 'person'];
export const taskOrder: readonly StepId[] = ['account', ...inputSteps, 'review'];
export const setupTasks: readonly SetupTask[] = [
  { id: 'account', title: 'Google 계정', defaultDetail: '로그인 계정 확인' },
  { id: 'company', title: '회사 정보', defaultDetail: '회사명' },
  { id: 'organization', title: '최상위 조직', defaultDetail: '조직명' },
  { id: 'location', title: '기본 근무지', defaultDetail: '근무지명' },
  { id: 'person', title: '본인 정보', defaultDetail: '법적 성 · 법적 이름 · 입사일' },
  { id: 'review', title: '입력 내용 확인', defaultDetail: '조직 및 관리자 생성' },
];

export const emptyDraft: InitialAdminSetupInput = {
  companyName: '',
  rootOrganizationName: '',
  locationName: '',
  legalFamilyName: '',
  legalGivenName: '',
  hireDate: '',
};

export const minimumHireDate = '1900-01-01';

export function normalizeInitialAdminSetupInput(input: InitialAdminSetupInput): InitialAdminSetupInput {
  return {
    companyName: input.companyName.trim(),
    rootOrganizationName: input.rootOrganizationName.trim(),
    locationName: input.locationName.trim(),
    legalFamilyName: input.legalFamilyName.trim(),
    legalGivenName: input.legalGivenName.trim(),
    hireDate: input.hireDate.trim(),
  };
}

export function nameError(label: string, value: string, maxLength = 200) {
  const length = Array.from(value.trim()).length;
  if (length === 0) return `${label}을 입력해 주세요.`;
  if (length > maxLength) return `${label}은 ${maxLength}자 이하로 입력해 주세요.`;
  return undefined;
}

export function validName(value: string, maxLength = 200) {
  return nameError('이름', value, maxLength) === undefined;
}

export function hireDateError(value: string, today: string) {
  if (!value) return '입사일을 입력해 주세요.';
  if (value < minimumHireDate || value > today) return '1900-01-01부터 오늘 사이의 날짜를 입력해 주세요.';
  return undefined;
}

export function legalFullName(draft: InitialAdminSetupInput) {
  return `${draft.legalFamilyName.trim()}${draft.legalGivenName.trim()}`;
}
